import { RefObject, useCallback, useRef, useState } from "react";

import { clamp } from "lodash-es";
import { useLocalStorage } from "usehooks-ts";

import { useEventListener } from "src/hooks/usehooks-ts";

type UseResizableWidthOptions = {
  // Viewport edge the pane is anchored to. Determines cursor math:
  // "right" → dragging left grows width; "left" → dragging right grows width.
  side: "left" | "right";
  storageKey: string;
  minWidth: number;
  maxWidth: number;
  defaultWidth?: number;
  // Pane element to measure from. Without it the pane is assumed to be
  // anchored to the viewport edge given by `side`; pass it when other
  // content (e.g. the main nav sidebar) sits between the pane and that edge.
  elementRef?: RefObject<HTMLElement | null>;
};

type UseResizableWidthResult = {
  width: number;
  isResizing: boolean;
  handleResizeStart: () => void;
};

export const useResizableWidth = ({
  side,
  storageKey,
  minWidth,
  maxWidth,
  defaultWidth,
  elementRef,
}: UseResizableWidthOptions): UseResizableWidthResult => {
  const effectiveDefault = defaultWidth ?? (minWidth + maxWidth) / 2;
  const [width, setWidth] = useLocalStorage<number>(
    storageKey,
    effectiveDefault,
  );
  const clampedWidth = clamp(width, minWidth, maxWidth);
  const [isResizing, setIsResizing] = useState(false);
  const animationFrameRef = useRef<number | null>(null);

  // keep-memo(react-compiler): returned to callers in mainPage, analytics and
  // entities, which are not compiled yet and rely on a stable identity.
  const handleResizeStart = useCallback(() => {
    setIsResizing(true);
  }, []);

  const getRawWidth = (event: MouseEvent) => {
    const rect = elementRef?.current?.getBoundingClientRect();
    return side === "right"
      ? (rect?.right ?? window.innerWidth) - event.clientX
      : event.clientX - (rect?.left ?? 0);
  };

  useEventListener("mousemove", (event: MouseEvent) => {
    if (!isResizing) return;

    // Cancel any pending animation frame
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    // Schedule the update for the next animation frame
    animationFrameRef.current = requestAnimationFrame(() => {
      setWidth(clamp(getRawWidth(event), minWidth, maxWidth));
      animationFrameRef.current = null;
    });
  });

  useEventListener("mouseup", (event: MouseEvent) => {
    if (!isResizing) return;

    // Clean up any pending animation frame
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Apply the final position to avoid losing the last width update
    setWidth(clamp(getRawWidth(event), minWidth, maxWidth));

    setIsResizing(false);
  });

  return { width: clampedWidth, isResizing, handleResizeStart };
};
